import { Icon } from '@iconify/react';
import { useState, useEffect, useRef } from 'react';
import { Message, RootState, updateMessageFeedback, useAppDispatch, useUpdateFeedbackMutation, updateSelectedMessage, SETTING_TYPE, useUpdateGuestFeedbackMutation } from '@circuitry-ai/doc-data';
import { CtyButton, CtyShowToast, checkType, SpeechUtility, ArticleModel, ArticleType } from '@circuitry-ai/doc-ui';
import { useClipboard } from "@nextui-org/use-clipboard";
import { CommentModal } from './chatcommentmodal';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import TranslateModal from './translateModal';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from '@nextui-org/react';
import { ExpertAnswer, chatOptionsDialogType } from './feedbackoptions/expertoption';

export enum feedbackOptions {
    READ = 'Read',
    COPY = 'Copy',
    COMMENT = 'Comment',
    LIKE = 'Like',
    DISLIKE = 'Dislike',
    EXPERT_ANSWER = 'Expert Answer',
    TRANSLATE = 'Translate',
    DEEP_THINK = 'Deep Think',
}



export type FeedbackOptionsType = typeof feedbackOptions[keyof typeof feedbackOptions];

interface AdvisorFeedbadProps {
    message: Partial<Message>,
    isSpeechMode: boolean,
    translatedText: string,
    isAdvisorView: boolean,
    chatAdvisor: any,
    getAdvisorFeedbackRefetch?: () => void;
    setIsSpeechMode: (value: boolean) => void;
    msgIndex: number;
    converseSenderId?: string;
    /** Copy/read are rendered outside the message bubble on hover. */
    hideCopyRead?: boolean;
    /** Show feedback icons only when the message row is hovered. */
    showOnHover?: boolean;
}



interface CommentProps {
    message: Partial<Message>;
    setShowCommentDialog: React.Dispatch<React.SetStateAction<chatOptionsDialogType | null>>;
}

const Comment: React.FC<CommentProps> = ({ message, setShowCommentDialog }) => {
    // Check if the message has comment history or a comment
    const hasComments = Boolean(
        (message?.comment_history && message.comment_history.length > 0) ||
        (message?.comment && message.comment.trim() !== '')
    );

    if ((message?.metaData ?? [])?.length === 0) {
        return null;
    }

    return (
        <CtyButton
            tooltipContent={feedbackOptions.COMMENT}
            toolTipColor='warning'
            id={`comment-btn-${message?.isAdvisor && message?.metaData?.length ? message?.metaData?.[6]?.message_id : 'greet'}`}
            isIconOnly
            radius="full"
            size="sm"
            variant="light"
            onPress={() => setShowCommentDialog(chatOptionsDialogType.COMMENT)}
        >
            <Icon
                icon={hasComments ? "fluent:comment-24-filled" : "fluent:comment-24-regular"}
                width={16}
                height={16}
                style={hasComments ? { color: "#0070f0" } : {}}
            />
        </CtyButton>
    );
};

interface ReadProps {
    isReading: boolean,
    isAdvisorView: boolean,
    setIsReading: React.Dispatch<React.SetStateAction<boolean>>;
    message: Partial<Message>,
}

const Read: React.FC<ReadProps> = ({ isReading, setIsReading, message, isAdvisorView }) => {
    const dispatch = useAppDispatch();
    const { advisorAbilities } = useSelector((state: RootState) => state.advisor);
    const isTranslationActive = advisorAbilities?.[SETTING_TYPE.SPEAK]?.is_active ?? false;

    if (!isAdvisorView || !isTranslationActive) {
        return null;
    }

    const handleRead = (async () => {
        if (isReading) {
            void (await SpeechUtility.getInstance(dispatch)).handleMute(isReading, setIsReading);
        } else {
            void (await SpeechUtility.getInstance(dispatch)).textToSpeech(message.message ?? '', setIsReading);
        }
    });

    return (
        <CtyButton
            tooltipContent={feedbackOptions.READ}
            toolTipColor='warning'
            id={`speech-btn-${message?.isAdvisor && message?.metaData?.length ? message?.metaData?.[6]?.message_id : 'greet'}`}
            isIconOnly
            radius="full"
            size="sm"
            variant="light"
            onPress={handleRead}
        >
            <div className="relative">
                {isReading && <Spinner className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2" />}
                <Icon icon="bi:volume-down" height={20} width={20} />
            </div>
        </CtyButton>
    );
};

interface CopyProps {
    message: Partial<Message>,
    translatedText: string,
}

const Copy: React.FC<CopyProps> = ({ message, translatedText }) => {
    const dispatch = useAppDispatch();
    const { copied, copy } = useClipboard();
    const handleCopy = () => {
        copy(translatedText);
        dispatch(updateSelectedMessage(translatedText));
    };
    return (
        <CtyButton
            tooltipContent={feedbackOptions.COPY}
            toolTipColor='warning'
            id={`copy-btn-${message?.isAdvisor && message?.metaData?.length ? message?.metaData?.[6]?.message_id : 'greet'}`}
            isIconOnly
            radius="full"
            size="sm"
            variant="light"
            onPress={() => handleCopy()}
        >
            {copied ? (
                <Icon
                    className="text-lg text-default-600"
                    icon="gravity-ui:check"
                />
            ) : (
                <Icon
                    icon="lets-icons:copy-light"
                    height={20}
                    width={20}
                />
            )}
        </CtyButton>
    );
};

interface TranslationOptionProps {
    message: Partial<Message>,
    isAdvisorView: boolean,
    setShowCommentDialog: React.Dispatch<React.SetStateAction<chatOptionsDialogType | null>>;
}

const TranslationOption: React.FC<TranslationOptionProps> = ({ message, setShowCommentDialog, isAdvisorView }) => {
    const { advisorAbilities } = useSelector((state: RootState) => state.advisor);
    const isTranslationActive = advisorAbilities?.[SETTING_TYPE.TRANSLATION]?.is_active ?? false;

    if (!isAdvisorView || !isTranslationActive) {
        return null;
    }

    return (
        <CtyButton
            tooltipContent={feedbackOptions.TRANSLATE}
            toolTipColor='warning'
            id={`translate-btn-${message?.isAdvisor && message?.metaData?.length ? message?.metaData?.[6]?.message_id : 'greet'}`}
            isIconOnly
            radius="full"
            size="sm"
            variant="light"
            onPress={() => setShowCommentDialog(chatOptionsDialogType.TRANSLATE)}
        >
            <Icon icon="bi:translate" width="1.2rem" height="1.2rem" className="text-black dark:text-[#f5f5f5]" />
        </CtyButton>
    );
};

interface LikeDislikeProps {
    feedbackType: 'like' | 'dislike',
    localStateFeedback: string,
    message: Partial<Message>,
    onFeedBack: (currentFeedbackState: string, prevFeedbackState: string) => void;
    advisor_id: string;
    msgIndex: number;
}

const LikeDislike: React.FC<LikeDislikeProps> = ({ feedbackType, localStateFeedback, message, onFeedBack, advisor_id, msgIndex }) => {
    const [showCommentDialog, setShowCommentDialog] = useState<boolean>(false);
    const isLike = feedbackType === 'like';
    const currentFeedback = localStateFeedback === feedbackType;
    const iconSize = isLike ? 19 : 16;

    if ((message?.metaData ?? [])?.length === 0) {
        return null;
    }

    const getIconProps = (isLike: boolean) => isLike
        ? { filledIcon: "gravity-ui:thumbs-up-fill", emptyIcon: "iconamoon:like-thin", color: 'green' }
        : { filledIcon: "gravity-ui:thumbs-down-fill", emptyIcon: "mynaui:dislike", color: '#ff0101' };

    const iconProps = getIconProps(isLike);

    const getButtonId = () => message?.isAdvisor && message?.metaData?.length
        ? message.metaData[6]?.message_id
        : 'greet';

    const getCurrentFeedbackState = () => {
        if (feedbackType === 'like' && currentFeedback) {
            return 'remove_like';
        }
        if (feedbackType === 'dislike' && currentFeedback) {
            return 'remove_dislike';
        }
        return feedbackType;
    };

    const handleClick = () => {
        const currentFeedbackState = getCurrentFeedbackState();
        if (feedbackType === 'dislike' && !currentFeedback) {
            setShowCommentDialog(true);
        } else {
            onFeedBack(currentFeedbackState, localStateFeedback);
        }
    };
    const isConversationHistoryPage = useLocation().pathname.includes('/conversation')
    return (
        <>
            <CtyButton
                tooltipContent={isLike ? feedbackOptions.LIKE : feedbackOptions.DISLIKE}
                toolTipColor='warning'
                id={`${feedbackType}-btn-${getButtonId()}`}
                isIconOnly
                radius="full"
                size="sm"
                variant="light"
                onPress={handleClick}
            >
                {currentFeedback ? (
                    <Icon
                        className="text-lg text-default-600"
                        style={{ color: iconProps.color }}
                        icon={iconProps.filledIcon}
                    />
                ) : (
                    <Icon
                        icon={iconProps.emptyIcon}
                        height={iconSize}
                        width={iconSize}
                    />
                )}
            </CtyButton>
            {showCommentDialog && (
                <CommentModal
                    data={message}
                    msgIndex={msgIndex}
                    isCommentFromHistoryPage={isConversationHistoryPage}
                    getHistoryRefetch={() => { }}
                    onDialogClose={() => {
                        setShowCommentDialog(false);
                        onFeedBack('dislike', localStateFeedback);
                    }}
                    advisor_id={advisor_id}
                />
            )}
        </>
    );
};

// Deep Think Modal
interface DeepThinkModalProps {
    isOpen: boolean;
    onClose: () => void;
    onRegenerate: () => void;
}

const DeepThinkModal: React.FC<DeepThinkModalProps> = ({ isOpen, onClose, onRegenerate }) => (
    <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent>
            <ModalHeader>Deep Think Mode</ModalHeader>
            <ModalBody>
                <div>Regenerate with Deep Think Mode (model: deep-think-v1)</div>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onPress={onRegenerate}>Regenerate</Button>
                <Button variant="light" onPress={onClose}>Cancel</Button>
            </ModalFooter>
        </ModalContent>
    </Modal>
);

const ChatOptions = ({
    message,
    translatedText,
    isSpeechMode,
    chatAdvisor,
    getAdvisorFeedbackRefetch,
    isAdvisorView,
    setIsSpeechMode,
    msgIndex,
    onDeepThinkRegenerate, // <-- add this prop for regeneration
    isLatestAdvisorMessage,
    converseSenderId,
    hideCopyRead = false,
    showOnHover = false,
}: AdvisorFeedbadProps & { onDeepThinkRegenerate?: () => void, isLatestAdvisorMessage?: boolean }) => {
    const dispatch = useAppDispatch();
    const hasMountedRef = useRef(false);
    // Only the conversation log page supplies a refetch; the chat widget can float over a
    // /conversation route without one and must keep using its own live session.
    const isConversationHistoryPage = useLocation().pathname.includes('/conversation') && !!getAdvisorFeedbackRefetch;
    const [updateFeedback] = useUpdateFeedbackMutation();
    const [updateGuestFeedback] = useUpdateGuestFeedbackMutation();
    const [showCommentDialog, setShowCommentDialog] = useState<chatOptionsDialogType | null>(null);
    const [isReading, setIsReading] = useState<boolean>(false);
    const { guestToken } = useSelector((state: RootState) => state.advisor);
    const { token } = useSelector((state: RootState) => state.auth);
    const [showDeepThinkModal, setShowDeepThinkModal] = useState(false);
    const [expertAnswerLoading, setExpertAnswerLoading] = useState(false);
    const feedbackOptionsList = [
        ...(hideCopyRead ? [] : [feedbackOptions.READ, feedbackOptions.COPY]),
        feedbackOptions.TRANSLATE,
        feedbackOptions.COMMENT,
        feedbackOptions.LIKE,
        feedbackOptions.DISLIKE,
        feedbackOptions.EXPERT_ANSWER,
        feedbackOptions.DEEP_THINK,
    ];

    const [localStateFeedback, setLocalStateFeedback] = useState<string>(message?.feedback_history?.[0]?.feedback_type ?? '');
    useEffect(() => {
        if (message?.feedback_history?.[0]?.feedback_type !== localStateFeedback) {
            setLocalStateFeedback(message?.feedback_history?.[0]?.feedback_type ?? '');
        }
    }, [message?.feedback_history?.[0]?.feedback_type]);

    useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            if (isSpeechMode && message?.isAdvisor) {
                void (async () => {
                    void (await SpeechUtility.getInstance(dispatch)).textToSpeech(
                        message?.message ?? '',
                        setIsReading,
                        setIsSpeechMode
                    );
                })();
            }
        }
    }, [dispatch, isSpeechMode, message?.isAdvisor, message.message]);
    const { sender_id } = useParams();
    const { currentActiveSocketObject } = useSelector((state: RootState) => state.chatAdvisorSliceReducer);

    const resolveSenderId = () => (
        isConversationHistoryPage
            ? (chatAdvisor?.sender_id ?? window.atob(sender_id ?? ''))
            : (message?.metaData?.[2]?.session_id ?? currentActiveSocketObject?.id ?? converseSenderId ?? '')
    );

    // conversation_log.message_id (history) or live messageId — not metaData[n]
    const message_id=message?.metaData?.find((item:any)=>item?.message_id)?.message_id ?? ''

    const onFeedBack = async (currentFeedbackState: string, prevFeedbackState: string) => {
        setLocalStateFeedback(currentFeedbackState);
        const feedbackPayload : any = {
            feedback: currentFeedbackState,
            message_id,
            sender_id: resolveSenderId(),
            advisor_id: chatAdvisor?.advisor_id,
        };
        const apiPayLoad = {
            payLoad: feedbackPayload,
            token: guestToken
        }
        try {
            const response = token ? await updateFeedback(feedbackPayload).unwrap() : await updateGuestFeedback(apiPayLoad).unwrap();
            CtyShowToast(checkType.success, response?.message ?? 'feedback updated successfully');
            (isConversationHistoryPage && getAdvisorFeedbackRefetch) ? getAdvisorFeedbackRefetch() : dispatch(updateMessageFeedback({ messageId:message_id, feedback: currentFeedbackState }));
        } catch (e) {
            CtyShowToast(checkType.error, (e as Error)?.message ?? 'Something went wrong!');
            setLocalStateFeedback(prevFeedbackState);
            (isConversationHistoryPage && getAdvisorFeedbackRefetch) ? getAdvisorFeedbackRefetch() : dispatch(updateMessageFeedback({ messageId:message_id, feedback: prevFeedbackState }));

        }
    };

    const renderFeedback = (option: FeedbackOptionsType) => {
        switch (option) {
            case feedbackOptions.READ:
                return <Read
                    isReading={isReading}
                    setIsReading={setIsReading}
                    message={message}
                    isAdvisorView={isAdvisorView}
                />;
            case feedbackOptions.COPY:
                return <Copy
                    message={message}
                    translatedText={translatedText} />;
            case feedbackOptions.COMMENT:
                return <Comment
                    message={message}
                    setShowCommentDialog={setShowCommentDialog}
                />;
            case feedbackOptions.LIKE:
                return <LikeDislike
                    feedbackType="like"
                    message={message}
                    localStateFeedback={localStateFeedback}
                    onFeedBack={onFeedBack}
                    advisor_id={chatAdvisor?.advisor_id}
                    msgIndex={msgIndex} />;
            case feedbackOptions.DISLIKE:
                return <LikeDislike
                    feedbackType="dislike"
                    message={message}
                    localStateFeedback={localStateFeedback}
                    onFeedBack={onFeedBack}
                    advisor_id={chatAdvisor?.advisor_id}
                    msgIndex={msgIndex}
                />;
            case feedbackOptions.EXPERT_ANSWER:
                return (
                    <ExpertAnswer
                        message={message}
                        advisor_id={chatAdvisor?.advisor_id}
                        sender_id={resolveSenderId()}
                    />
                );
            case feedbackOptions.TRANSLATE:
                return <TranslationOption
                    message={message}
                    setShowCommentDialog={setShowCommentDialog}
                    isAdvisorView={isAdvisorView}
                />;
            // case feedbackOptions.DEEP_THINK:
            //     if (message?.isAdvisor && isLatestAdvisorMessage) {
            //         return (
            //             <CtyButton
            //                 tooltipContent={feedbackOptions.DEEP_THINK}
            //                 toolTipColor='warning'
            //                 id={`deepthink-btn-${message?.metaData?.length ? message?.metaData?.[6]?.message_id : 'greet'}`}
            //                 isIconOnly
            //                 radius="full"
            //                 size="sm"
            //                 variant="light"
            //                 onPress={() => setShowDeepThinkModal(true)}
            //             >
            //                 <Icon icon="mdi:brain" height={18} width={18} />
            //             </CtyButton>
            //         );
            //     }
            //     return null;
        }
    };



    return (
        <div
            className={`w-fit flex rounded-full bg-content1 chat-feedback-container border border-default-200 px-1 mt-1 ${
                showOnHover ? 'opacity-0 group-hover/msg:opacity-100 transition-opacity duration-150' : ''
            }`}
        >
            {feedbackOptionsList.map((option: FeedbackOptionsType) => {
                return <div key={option}>
                    {renderFeedback(option)}
                </div>;
            })}
            {showCommentDialog === chatOptionsDialogType.COMMENT && (
                <CommentModal
                    data={message}
                    msgIndex={msgIndex}
                    isCommentFromHistoryPage={isConversationHistoryPage}
                    getHistoryRefetch={getAdvisorFeedbackRefetch}
                    onDialogClose={() => setShowCommentDialog(null)}
                    advisor_id={chatAdvisor?.advisor_id}
                    chatAdvisor={chatAdvisor}
                />
            )}
            {showCommentDialog === chatOptionsDialogType.TRANSLATE && (
                <TranslateModal
                    message={translatedText ?? ''}
                    onDialogClose={() => setShowCommentDialog(null)}
                />
            )}
            {showCommentDialog === chatOptionsDialogType.EXPERT_ANSWER && (
                <ArticleModel
                    onClose={() => setShowCommentDialog(null)}
                    type={ArticleType.EXPERT_ANSWER}
                    advisorId={chatAdvisor?.advisor_id}
                    message={message}
                    setExpertAnswerLoading={setExpertAnswerLoading}
                />
            )}
            <DeepThinkModal
                isOpen={showDeepThinkModal}
                onClose={() => setShowDeepThinkModal(false)}
                onRegenerate={() => {
                    setShowDeepThinkModal(false);
                    onDeepThinkRegenerate && onDeepThinkRegenerate();
                }}
            />
        </div>
    );
};

export { ChatOptions };